/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import find from 'lodash/find';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import uniqBy from 'lodash/uniqBy';
import values from 'lodash/values';
import { SecurityHelper } from 'sarsaparilla';
import axios from 'axios';
import * as types from '../../src/constants';
import { SET_IMAGE_URL, SHARED_PREFIX, apiUrls } from '../constants';
import { transformPermit } from '../../src/utils/enrich-backend-data';
import { fetchRecarea, fetchPermit as fetchLegacyPermit } from '../../src/actions/permit';
import { permitContentSelector } from '../slices/permitContent';

const { ISSUANCE_ENDPOINT, IS_LEGACY_PERMIT_V2 } = types;

export function fetchReservationSummaryImageUrlOk(url) {
    return {
        type: SHARED_PREFIX + SET_IMAGE_URL,
        payload: url,
    };
}

export function fetchReservationSummaryImageUrl(permitId) {
    return async (dispatch) => {
        try {
            const media = await axios.get(apiUrls.imageUrl(permitId), {});
            if (!media?.data.err && media?.data.result) {
                const photos = media?.data.result;
                const previewPhoto = find(photos, { is_preview: true });
                dispatch(fetchReservationSummaryImageUrlOk(previewPhoto.url));
            }
        } catch (_) {
            // handle unable to retrieve image url
            // no dispatch required as component defaults
        }
    };
}

//Used for account reservation items
export function fetchAllIssuances(issuanceId, permitId) {
    return (dispatch, getState) => {
        const permitMapping = permitContentSelector.permitMapping(getState());
        return axios
            .get(ISSUANCE_ENDPOINT(permitId, issuanceId, permitMapping), {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((success) => {
                dispatch({
                    type: types.GET_ALL_ISSUANCES,
                    payload: success.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_ALL_ISSUANCES_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                        error_code: error.response.status,
                    });
                }
            });
    };
}

export function fetchIssuance(issuanceId, permitId) {
    return (dispatch, getState) => {
        const permitMapping = permitContentSelector.permitMapping(getState());
        return axios
            .get(ISSUANCE_ENDPOINT(permitId, issuanceId, permitMapping), {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((success) => {
                dispatch({
                    type: types.GET_ISSUANCE,
                    payload: success.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_ISSUANCE_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                        error_code: error.response.status,
                    });
                }
            });
    };
}

export { fetchRecarea };

export function fetchInyoPermitRules(permit) {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/permitinyo/${permit.id}/rules`)
            .then((success) => {
                dispatch({
                    type: types.GET_PERMIT_RULES,
                    payload: success.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_PERMIT_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function fetchInyoPermitSeasons(permit) {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/permitinyo/${permit.id}/seasons`)
            .then((success) => {
                dispatch({
                    type: types.GET_PERMIT_SEASONS,
                    payload: success.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_PERMIT_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function fetchPermitRules(permit) {
    const mapping = {
        233262: fetchInyoPermitRules,
    };

    if (isFunction(mapping[permit.id])) {
        mapping[permit.id](permit);
    }
}

export function fetchPermitSeasons(permit) {
    const mapping = {
        233262: fetchInyoPermitSeasons,
    };
    if (isFunction(mapping[permit.id])) {
        mapping[permit.id](permit);
    }
}

export function fetchAreasFromPermit(permit) {
    return (dispatch) => {
        const uniq = uniqBy(values(permit.divisions), 'district');
        const divisions = uniq.map((div) => get(div, 'district'));
        dispatch({
            type: types.SET_DISTRICTS,
            payload: divisions,
        });
    };
}

export function fetchPermit(permitId) {
    return (dispatch, getState) => {
        const permitMapping = permitContentSelector.permitMapping(getState());
        if (IS_LEGACY_PERMIT_V2(permitId, permitMapping)) {
            return dispatch(fetchLegacyPermit(permitId));
        }

        return axios
            .get(`${process.env.API}/permitcontent/${permitId}`)
            .then((success) => {
                const permit = transformPermit(success.data.payload);
                dispatch({
                    type: types.GET_PERMIT,
                    payload: permit,
                });
                dispatch(fetchRecarea(permit.recarea));
                dispatch(fetchAreasFromPermit(permit));
                dispatch(fetchPermitRules(permit));
                dispatch(fetchPermitSeasons(permit));
                return Promise.resolve(permit);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_PERMIT_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function getExpressMembershipsById(permitId, userId) {
    const apiUrl = `${process.env.API}/permitissuance/${permitId}/express-memberships/${userId}`;
    return (dispatch) => {
        return axios
            .get(apiUrl, { headers: { Authorization: SecurityHelper.getAuthHeader() } })
            .then((response) => {
                const newAction = {
                    type: types.GET_EXPRESS_MEMBERSHIPS_FOR_PERMIT_BY_ID,
                    payload: response.data.payload,
                };
                dispatch(newAction);
            })
            .catch(() => {
                dispatch({
                    type: types.GET_EXPRESS_MEMBERSHIPS_FOR_PERMIT_BY_ID_ERROR,
                    payload: 'error fetching permit express membership by id',
                    error: true,
                });
            });
    };
}

//Used for account reservation items
export function getAllExpressMembershipsById(permitId, userId) {
    const apiUrl = `${process.env.API}/permitissuance/${permitId}/express-memberships/${userId}`;
    return (dispatch) => {
        return axios
            .get(apiUrl, { headers: { Authorization: SecurityHelper.getAuthHeader() } })
            .then((response) => {
                const newAction = {
                    type: types.GET_ALL_EXPRESS_MEMBERSHIPS_FOR_PERMIT_BY_ID,
                    payload: response.data.payload,
                };
                dispatch(newAction);
            })
            .catch(() => {
                dispatch({
                    type: types.GET_ALL_EXPRESS_MEMBERSHIPS_FOR_PERMIT_BY_ID_ERROR,
                    payload: 'error fetching permit express membership by id',
                    error: true,
                });
            });
    };
}

export function getExpressMembershipPrintDate(permitId, issuanceId) {
    const apiUrl = `${process.env.API}/permitissuance/${permitId}/issuances/${issuanceId}/printdate`;
    return (dispatch) => {
        return axios
            .get(apiUrl, { headers: { Authorization: SecurityHelper.getAuthHeader() } })
            .then((response) => {
                const newAction = {
                    type: types.GET_EXPRESS_MEMBERSHIP_PRINT_DATE_FOR_PERMIT_BY_ISSUANCE_ID,
                    permit_id: permitId,
                    issuance_id: issuanceId,
                    payload: response.data.payload,
                };

                dispatch(newAction);
            })
            .catch(() => {
                dispatch({
                    type: types.GET_EXPRESS_MEMBERSHIP_PRINT_DATE_FOR_PERMIT_BY_ISSUANCE_ID_ERROR,
                    payload: 'error fetching permit express membership print date',
                    error: true,
                });
            });
    };
}

//Used for account reservation items
export function fetchAllPermits(permitId) {
    return (dispatch, getState) => {
        const permitMapping = permitContentSelector.permitMapping(getState());
        if (IS_LEGACY_PERMIT_V2(permitId, permitMapping)) {
            return dispatch(fetchLegacyPermit(permitId));
        }

        return axios
            .get(`${process.env.API}/permitcontent/${permitId}`)
            .then((success) => {
                const permit = transformPermit(success.data.payload);
                dispatch({
                    type: types.GET_ALL_PERMITS,
                    payload: permit,
                });
                dispatch(fetchRecarea(permit.recarea));
                dispatch(fetchAreasFromPermit(permit));
                dispatch(fetchPermitRules(permit));
                dispatch(fetchPermitSeasons(permit));
                return Promise.resolve(permit);
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_ALL_PERMITS_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
            });
    };
}
